import React, {useState} from 'react';

import {Anchor, Icon, Skeleton, Text} from '@albert-io/atomic';
import EmptyState from 'client/Classrooms/Classroom/Teacher/EmptyState/index.react';
import MegaClassesEmptyState from 'client/Classrooms/Classroom/Teacher/MegaClassesEmptyState/MegaClassesEmptyState.react';

import {ErrorRetryState} from '../../components/ErrorRetryState';
import {useClassContentRevealed, useReportClassContentReady} from '../useClassContentReady';
import {ClassOverviewScopeBar} from './ClassOverviewScopeBar';
import {MasteryColumns} from './MasteryColumns';
import {OverviewSortMenu} from './OverviewSortMenu';
import type {OverviewSortKey} from './OverviewSortMenu';
import {StatTiles} from './StatTiles';
import {useClassOverview} from './useClassOverview';

interface Props {
  classroomId: string;
}

// Body below the scope bar: the stat-tiles grid (grid-cols-1 xs:grid-cols-3) + the accuracy section.
const OverviewBodySkeleton = () => (
  <>
    <div aria-hidden='true' className='xs:grid-cols-3 gap-x3 gap-y-x4 grid grid-cols-1'>
      {['t1', 't2', 't3'].map((key) => (
        <Skeleton className='rounded-x1 h-[124px] w-full' key={key} />
      ))}
    </div>
    <hr className='m-x0 border-border-primary border-0 border-t' />
    <div className='gap-x2 flex flex-col'>
      <Skeleton className='h-x4 w-[160px] rounded-full' />
      <Skeleton className='rounded-x1 h-[340px] w-full' />
    </div>
  </>
);

// Full initial skeleton: scope bar + body, matching the loaded layout so the reveal fills in place.
const OverviewSkeleton = () => (
  <div aria-busy='true' className='gap-x4 pt-x4 flex flex-col'>
    <span className='sr-only' role='status'>
      Loading overview…
    </span>
    <div aria-hidden='true' className='pb-x4-half gap-x2 flex flex-wrap'>
      <Skeleton className='h-x5 w-[220px] rounded-full' />
      <Skeleton className='h-x5 w-[160px] rounded-full' />
      <Skeleton className='h-x5 w-[160px] rounded-full' />
    </div>
    <OverviewBodySkeleton />
  </div>
);

// v2 Overview tab: class-level stat tiles + a mastery-bucketed student roster, scoped by
// subject/attempt/date. Flux-free port of the legacy Teacher/TeacherDashboard overview.
export const Overview = ({classroomId}: Props) => {
  const overview = useClassOverview(classroomId);
  const [sortBy, setSortBy] = useState<OverviewSortKey>('name');

  // Report when the overview reaches a terminal state (content, empty, or error); the shell reveals
  // the header + this tab together on `revealed`. Filter reloads (isLoadingStudents after first
  // paint) keep the page revealed — the shell's report is sticky per class.
  const revealed = useClassContentRevealed();
  const isContentPending =
    !overview.hasError &&
    (!overview.classroomReady ||
      (!overview.isMegaClass && !overview.hasNoSubjects && overview.isLoadingStudents));
  useReportClassContentReady(!isContentPending);

  // Held behind one skeleton until the shell reveals the whole page (banner + content) together.
  if (!revealed) {
    return <OverviewSkeleton />;
  }

  if (overview.hasError) {
    return (
      <div className='pt-x4'>
        <ErrorRetryState copy='We couldn’t load this class’s overview.' onRetry={overview.reload} />
      </div>
    );
  }

  // Post-reveal reload (a retry that flips classroomReady): keep the tab's own skeleton, header stays.
  if (!overview.classroomReady) {
    return <OverviewSkeleton />;
  }

  if (overview.isMegaClass) {
    return <MegaClassesEmptyState supportingTitleText='Overview data will not load' />;
  }

  if (overview.hasNoSubjects) {
    return (
      <EmptyState
        title='No overview data'
        body='Classroom data will appear when students answer questions. Visit the library to assign questions.'
      />
    );
  }

  return (
    <div className='gap-x4 pt-x4 flex flex-col'>
      <ClassOverviewScopeBar overview={overview} />
      {overview.isLoadingStudents ? (
        <div aria-busy='true' className='gap-x4 flex flex-col'>
          <span className='sr-only' role='status'>
            Loading overview data…
          </span>
          <OverviewBodySkeleton />
        </div>
      ) : (
        <>
          <StatTiles tiles={overview.tiles} />
          <hr className='m-x0 border-border-primary border-0 border-t' />
          <div className='flex flex-col'>
            <div className='flex items-center justify-between'>
              <Text as='h2' bold size='l'>
                Accuracy
              </Text>
              <div className='content-link flex items-center font-semibold'>
                <span aria-hidden>Sort by</span>
                <OverviewSortMenu sortBy={sortBy} onChange={setSortBy} />
              </div>
            </div>
            <Text size='s' color='secondary' className='mt-x1'>
              Accuracy excludes partial credit, for a more detailed breakdown, check out{' '}
              <Anchor to={`/reports/classrooms/${classroomId}/students`}>
                reports <Icon icon='arrow-up-right-from-square' iconStyle='regular' />
              </Anchor>
            </Text>
            <hr className='mt-x1 border-border-primary border-0 border-t' />
            {overview.students && (
              <div className='mt-x4'>
                <MasteryColumns
                  students={overview.students}
                  classroomId={classroomId}
                  sortBy={sortBy}
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
